/**
 * Parses a markdown table from text into an array of row objects.
 * Used as fallback when ERAX response has no erax_data but includes a table in content.
 *
 * Supports:
 *   | Col A | Col B | Col C |
 *   |-------|-------|-------|
 *   | 2025  | 6     | 8360  |
 */
export function parseMarkdownTable(text: string): Record<string, unknown>[] | null {
  if (!text || typeof text !== 'string') return null;

  const lines = text.split(/\r?\n/);
  const tableLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    // Table row: starts with | and contains at least one more |
    if (/^\|.+\|/.test(trimmed)) {
      tableLines.push(trimmed);
    } else if (tableLines.length > 0) {
      // End of table block
      break;
    }
  }

  if (tableLines.length < 2) return null;

  const parseRow = (row: string): string[] =>
    row
      .split('|')
      .map((cell) => cell.trim())
      .filter((_, i, arr) => i > 0 && i < arr.length - 1);

  const headerRow = parseRow(tableLines[0]);
  if (headerRow.length === 0) return null;

  // Skip separator line (|---|---|)
  const dataStart = /^[\s|:-]+$/.test(parseRow(tableLines[1]).join('')) ? 2 : 1;
  const headers = headerRow.map((h, idx) =>
    h.replace(/[:|]/g, '').replace(/\s+/g, ' ').trim() || `col_${idx}`,
  );

  const result: Record<string, unknown>[] = [];
  for (let i = dataStart; i < tableLines.length; i++) {
    const cells = parseRow(tableLines[i]);
    if (cells.length === 0) continue;
    const row: Record<string, unknown> = {};
    headers.forEach((key, j) => {
      const raw = cells[j] ?? '';
      row[key] = parseCell(raw);
    });
    result.push(row);
  }

  return result.length > 0 ? result : null;
}

function parseCell(raw: string): unknown {
  const t = raw.trim();
  if (t === '' || t.toLowerCase() === 'null') return null;
  // Strip commas for numeric check (e.g. "8,360" -> 8360)
  const cleanNum = t.replace(/,/g, '');
  const num = Number(cleanNum);
  if (!Number.isNaN(num) && cleanNum !== '') return num;
  return t;
}
